import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import LowPowerBannerImage from "../../../../assets/services/asic-design/asicDesign2.png";
import ProfileImage from "../../../../assets/blogpages/person.png";
import "./LowPowerDesign.css";
import { AnimatePresence, motion } from "framer-motion";

// Simple highlight component: wraps matches in <mark>
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? <mark key={i}>{part}</mark> : part
  );
};

const LowPowerDesign = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const closeOverlay = () => setShowPopup(false);

  const [replyVisible, setReplyVisible] = useState({});
  const toggleReply = (id) =>
    setReplyVisible(prev => ({ ...prev, [id]: !prev[id] }));

  const sectionRefs = {
    intro: useRef(null),
    objectives: useRef(null),
    clockGating: useRef(null),
    multiVoltage: useRef(null),
    powerGating: useRef(null),
    dvfs: useRef(null),
    estimation: useRef(null),
    physical: useRef(null),
    verification: useRef(null),
    trends: useRef(null),
    comments: useRef(null),
  };

  const scrollTo = ref =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  const handleSearch = () => setSearchTerm(searchInput.trim());
  useEffect(() => {
    if (!searchTerm) return;
    const firstMark = document.querySelector("mark");
    firstMark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  // fade in/out
  useEffect(() => {
    const io = new IntersectionObserver(
      entries =>
        entries.forEach(e =>
          e.isIntersecting
            ? e.target.classList.add("visible")
            : e.target.classList.remove("visible")
        ),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // auto-dismiss popup
  useEffect(() => {
    if (showPopup) {
      const t = setTimeout(() => setShowPopup(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showPopup]);

  const handleReplySubmit = e => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };
  const handleCommentSubmit = e => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };

  return (
    <div className="blog-post-page low-power-design">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>Low-Power ASIC Design</h1>
          <div className="breadcrumb">
            <Link to="/services/asic-design">Services</Link> | Low-Power Design
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            type="text"
            placeholder="Search keyword..."
            className="search-input"
          />
          <button onClick={handleSearch} className="search-button">
            Search
          </button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    objectives: "Power Objectives",
                    clockGating: "Clock Gating",
                    multiVoltage: "Multi-Voltage Domains",
                    powerGating: "Power Gating",
                    dvfs: "DVFS",
                    estimation: "High-Level Estimation",
                    physical: "Physical Low-Power Flow",
                    verification: "Power-Aware Verification",
                    trends: "Future Trends",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="blog-content-container">
        {/* Left */}
        <div className="blog-left-column">
          <img
            className="post-main-image fade-section"
            src={LowPowerBannerImage}
            alt="Low Power Design"
          />

          <div className="post-info-row fade-section">
            <span className="post-author">By John Doe</span>
            <span className="post-category">Category: Low-Power</span>
            <button className="comment-link" onClick={() => scrollTo(sectionRefs.comments)}>
              2 Comments
            </button>
          </div>

          {/* 1. Introduction */}
          <h2 ref={sectionRefs.intro} className="fade-section">
            Introduction to Low-Power Design
          </h2>
          <p className="fade-section">
            <Highlight
              text="Low-power ASIC design focuses on reducing static and dynamic power consumption throughout the design flow, from architecture to physical implementation. With rising demand for energy‐efficient devices—mobile, IoT, automotive—power budget is as critical as performance."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Lowering power extends battery life, reduces thermal stress, and cuts operating costs in large data centers. Modern flows include architectural techniques, RTL optimizations, synthesis constraints, and advanced place-and-route strategies tailored for low-power metrics."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Designers target two main contributors: dynamic power, driven by switching activity and capacitance, and leakage power, arising from transistor off‐state currents. Each requires different techniques and trade-offs to optimize effectively."
              query={searchTerm}
            />
          </p>

          {/* 2. Power Objectives */}
          <h2 ref={sectionRefs.objectives} className="fade-section">
            Defining Power Objectives
          </h2>
          <p className="fade-section">
            <Highlight
              text="At project outset, set clear power targets: active mode average and peak, standby leakage, and wake-up latency. Break these into block budgets for CPU, memory, I/O, and accelerators. Use early power models to validate architecture decisions."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Power intent artifacts (Unified Power Format, UPF) capture domain definitions, retention strategies, and isolation rules. Accurate UPF ensures EDA tools preserve low-power logic across synthesis, implementation, and verification."
              query={searchTerm}
            />
          </p>

          {/* 3. Clock Gating */}
          <h2 ref={sectionRefs.clockGating} className="fade-section">
            Clock Gating Techniques
          </h2>
          <p className="fade-section">
            <Highlight
              text="Clock gating disables the clock to registers or functional blocks when idle, reducing dynamic switching power. Smart insertion during RTL (using clock-enable patterns) or automatically during synthesis balances area and power savings."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Fine-grained gating at register‐level saves more power but adds gating logic overhead. Coarse gating at block level simplifies verification but may leave micro-idle toggles. Hybrid approaches often maximize net savings."
              query={searchTerm}
            />
          </p>

          {/* 4. Multi-Voltage Domains */}
          <h2 ref={sectionRefs.multiVoltage} className="fade-section">
            Multi-Voltage Domain Design
          </h2>
          <p className="fade-section">
            <Highlight
              text="Partitioning into multiple voltage islands allows critical blocks to run at higher VDD while non-critical sections drop to lower voltages. UPF annotations guide power switch insertion and isolation cell placement at domain boundaries."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Level shifters and isolation cells protect against undefined logic during domain crossings. Floorplanning must group domains physically to minimize routing complexity and power‐switching network overhead."
              query={searchTerm}
            />
          </p>

          {/* 5. Power Gating & Retention */}
          <h2 ref={sectionRefs.powerGating} className="fade-section">
            Power Gating & State Retention
          </h2>
          <p className="fade-section">
            <Highlight
              text="Power gating cuts off the supply rails entirely for deepest standby savings. Retention flip-flops maintain state through a lower supply during gate-off, allowing fast wake-up without full reinitialization."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Implement retention and isolation cells per UPF. Simulation must include power switch models; gate-level simulation with retention-aware vectors verifies correct behaviour across power-state transitions."
              query={searchTerm}
            />
          </p>

          {/* 6. Dynamic Voltage & Frequency Scaling */}
          <h2 ref={sectionRefs.dvfs} className="fade-section">
            Dynamic Voltage & Frequency Scaling (DVFS)
          </h2>
          <p className="fade-section">
            <Highlight
              text="DVFS adapts the operating point at runtime: reduce voltage and clock frequency under light load to save power, ramp up for performance bursts. Hardware monitors (PLL, performance counters) coordinate with on-chip controllers."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Implement multi‐corner DVFS flows in STA: each voltage/frequency pair requires its own SDC constraints and timing libraries to ensure safe operation across all modes."
              query={searchTerm}
            />
          </p>

          {/* 7. High-Level Power Estimation */}
          <h2 ref={sectionRefs.estimation} className="fade-section">
            High-Level Power Estimation
          </h2>
          <p className="fade-section">
            <Highlight
              text="Architectural power estimation using tools like McPAT or Cacti models block-level power before RTL. These early estimates guide trade-offs in algorithm selection, memory hierarchy, and pipeline depth for power efficiency."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Switch activity profiling from RTL simulation drives more accurate power numbers. Feeding switching data back into place-and-route tools improves physical optimization for realistic workloads."
              query={searchTerm}
            />
          </p>

          {/* 8. Physical Low-Power Flow */}
          <h2 ref={sectionRefs.physical} className="fade-section">
            Physical Implementation for Low-Power
          </h2>
          <p className="fade-section">
            <Highlight
              text="Low-power place-and-route uses multi-mode optimization: multiple SDC views for active, idle, and retention modes. ECO flows propagate UPF annotations through CTS, routing, and ECO stages."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Block-level power grids and switch insertion require careful floorplan planning. Post-route power analysis tools identify hotspots and leak paths for further optimization."
              query={searchTerm}
            />
          </p>

          {/* 9. Power-Aware Verification */}
          <h2 ref={sectionRefs.verification} className="fade-section">
            Power-Aware Verification
          </h2>
          <p className="fade-section">
            <Highlight
              text="Simulation must include power intent: verify UPF sequence, power switch off/on patterns, retention behaviour, and isolation correctness. Coverage metrics track power state transitions alongside functional coverage."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Formal checks validate isolation correctness and retention integrity across all defined power modes. Combining formal with simulation yields highest confidence in low-power designs."
              query={searchTerm}
            />
          </p>

          {/* 10. Future Trends */}
          <h2 ref={sectionRefs.trends} className="fade-section">
            Future Trends in Low-Power Design
          </h2>
          <p className="fade-section">
            <Highlight
              text="AI‐driven power optimization tools that learn from past designs to suggest gate-level or architecture-level power cuts automatically are emerging."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Emerging standards for power intent in ML accelerators and 3D ICs will extend UPF and CPF to new heterogeneous integration scenarios."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Ultra-low voltage SRAM and novel memory technologies promise to push standby leakage even lower in next-generation IoT chips."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>2 Comments</h3>

            {/* Comment 1 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Eve Energy</span>
                    <span className="comment-date">April 22, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Excellent breakdown of clock gating and UPF usage—very clear!
              </p>
              <button className="reply-button" onClick={() => toggleReply("eve")}>
                Reply
              </button>
              {replyVisible["eve"] && (
                <div className="leave-comment">
                  <h3>Reply to Eve Energy</h3>
                  <form onSubmit={handleReplySubmit}>
                    <div className="form-group">
                      <label>Name</label>
                      <input type="text" placeholder="Your name..." required />
                    </div>
                    <div className="form-group">
                      <label>Message</label>
                      <textarea placeholder="Your reply..." required />
                    </div>
                    <button type="submit" className="send-message-btn">
                      Reply
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Comment 2 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Frank Frugal</span>
                    <span className="comment-date">April 23, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Insightful future trends—looking forward to AI tools for power optimization!
              </p>
              <button className="reply-button" onClick={() => toggleReply("frank")}>
                Reply
              </button>
              {replyVisible["frank"] && (
                <div className="leave-comment">
                  <h3>Reply to Frank Frugal</h3>
                  <form onSubmit={handleReplySubmit}>
                    <div className="form-group">
                      <label>Name</label>
                      <input type="text" placeholder="Your name..." required />
                    </div>
                    <div className="form-group">
                      <label>Message</label>
                      <textarea placeholder="Your reply..." required />
                    </div>
                    <button type="submit" className="send-message-btn">
                      Reply
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Leave a comment */}
            <div className="leave-comment fade-section">
              <h3>Leave a comment</h3>
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here..." required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here..." required />
                </div>
                <button type="submit" className="send-message-btn">
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Desktop Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              type="text"
              placeholder="Search keyword..."
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button">
              Search
            </button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction",
                      objectives: "Power Objectives",
                      clockGating: "Clock Gating",
                      multiVoltage: "Multi-Voltage Domains",
                      powerGating: "Power Gating",
                      dvfs: "DVFS",
                      estimation: "High-Level Estimation",
                      physical: "Physical Low-Power Flow",
                      verification: "Power-Aware Verification",
                      trends: "Future Trends",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Thank You Popup */}
        <AnimatePresence>
          {showPopup && (
            <motion.div
              className="thank-you-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
            >
              <motion.div
                className="thank-you-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="checkmark-container">
                  <motion.svg
                    className="checkmark"
                    viewBox="0 0 52 52"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
                    <path
                      d="M16 27.5L23 34.5L36 19.5"
                      stroke="#007bff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </div>
                <h3>Thank you for the feedback!</h3>
                <p>We appreciate your comment.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LowPowerDesign;